import React, { useState } from 'react';
import { List, ChevronLeft, ChevronRight } from 'lucide-react';
import './DailyProgress.css';

interface ChecklistItem {
  id: number;
  label: string;
  completed: boolean;
}

interface SubGoal {
  id: number;
  label: string;
  completed: boolean;
}

const DailyProgress: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [checklist, setChecklist] = useState<ChecklistItem[]>([
    { id: 1, label: 'Take Insulin', completed: true },
    { id: 2, label: 'Eat Breakfast', completed: false },
    { id: 3, label: '30 Minutes of Exercise', completed: false },
    { id: 4, label: 'Drink 8 Glasses of Water', completed: false },
    { id: 5, label: 'Check Blood Glucose', completed: false },
    { id: 6, label: 'Track Carbohydrate Intake', completed: false },
    { id: 7, label: 'Log Weight', completed: false },
  ]);

  const [subGoals, setSubGoals] = useState<SubGoal[]>([
    { id: 1, label: 'Drink 8 cups of water', completed: false },
    { id: 2, label: 'Walk an extra 1,000 steps', completed: false },
    { id: 3, label: 'Wind down 30 minutes earlier', completed: false },
    { id: 4, label: 'Swap one processed meal for a whole food option', completed: false },
  ]);

  const toggleChecklistItem = (id: number) => {
    setChecklist(checklist.map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    ));
  };

  const toggleSubGoal = (id: number) => {
    setSubGoals(subGoals.map(goal =>
      goal.id === id ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const calculateProgress = () => {
    const completed = checklist.filter(item => item.completed).length;
    const total = checklist.length;
    return Math.round((completed / total) * 100);
  };

  const getCompletedSubGoals = () => {
    return subGoals.filter(goal => goal.completed).length;
  };

  const progress = calculateProgress();

  const getDateRange = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(selectedDate);
      date.setDate(date.getDate() - i);
      dates.push(date);
    }
    return dates;
  };

  const formatDateLabel = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    const today = new Date();
    if (newDate <= today) {
      setSelectedDate(newDate);
    }
  };

  return (
    <div className="daily-progress">
      <h1 className="daily-progress-title">Daily Progress</h1>

      {/* Date Navigation */}
      <div className="date-navigation">
        <button 
          className="nav-btn"
          onClick={goToPreviousDay}
          title="Previous day"
        >
          <ChevronLeft size={20} />
        </button>
        
        <div className="date-selector">
          {getDateRange().map((date, idx) => (
            <button
              key={idx}
              className={`date-btn ${isToday(date) ? 'today' : ''} ${date.toDateString() === selectedDate.toDateString() ? 'active' : ''}`}
              onClick={() => setSelectedDate(date)}
            >
              <span className="date-label">{formatDateLabel(date)}</span>
              <span className="date-number">{date.getDate()}</span>
            </button>
          ))}
        </div>

        <button 
          className="nav-btn"
          onClick={goToNextDay}
          disabled={selectedDate.toDateString() === new Date().toDateString()}
          title="Next day"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="progress-container">
        {/* Main Checklist */}
        <div className="progress-card">
          <h2 className="section-title">Checklist</h2>
          
          <div className="checklist">
            {checklist.map(item => (
              <label key={item.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={item.completed}
                  onChange={() => toggleChecklistItem(item.id)}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className={`checkbox-label ${item.completed ? 'completed' : ''}`}>
                  {item.label}
                </span>
              </label>
            ))}
          </div>

          <div className="progress-section">
            <h3 className="progress-label">Progress Toward Daily Goals</h3>
            <div className="progress-bar-container">
              <div className="progress-bar-bg">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${progress}%` }}
                >
                  <span className="progress-text">{progress}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Goals */}
        <div className="progress-card">
          <div className="subgoals-header">
            <List size={24} className="subgoals-icon" />
            <h2 className="section-title">Today's Sub-Goals</h2>
          </div>
          
          <div className="subgoals-list">
            {subGoals.map(goal => (
              <label key={goal.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={goal.completed}
                  onChange={() => toggleSubGoal(goal.id)}
                  className="checkbox-input"
                />
                <span className="checkbox-custom"></span>
                <span className={`checkbox-label ${goal.completed ? 'completed' : ''}`}>
                  {goal.label}
                </span>
              </label>
            ))}
          </div>

          <div className="subgoals-footer">
            {getCompletedSubGoals()} of {subGoals.length} complete
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyProgress;
